import React, { useState } from 'react'

import i18n from 'live/services/i18n'

import GlobalNotification from 'live/services/global-notification'
import { Notification } from '../Notification/Notification'

import getLookups from '../../../api/getLookups'
import getLookupValues from '../../../api/getLookupValues'
import { Entries } from '../../types/api/LiveLookupResponse'
import { GallerReplicationLookup } from '../../types/api/page'

import Toggle from '../SimpleInputs/Toggle'
import { OptionType } from '../../types/utilities'
import { CustomToolTip } from '../CustomToolTip/CustomToolTip'
import ListAsync from '../../../Form/components/LookupsList/ListAsync'
import { callbackLookup, callbackLookupValues } from '../../../Form/components/LookupsList/helper'

import { LookupButtonContainer, TemplateModal, CustomListAsyncLocal } from './styles'
import styled from 'styled-components'

interface ConfigLookupReplicationButtonProps {
    active: boolean
    lookup: GallerReplicationLookup
    onChange: (active: boolean, lookups: GallerReplicationLookup) => void
}

export function ConfigLookupReplicationButton({
    active = false,
    lookup = { showEmptyValue: false, fixedValues: {}, macro: '' },
    onChange
}: ConfigLookupReplicationButtonProps) {
    const [lookupStatus, setlookupStatus] = useState(active || false)
    const [showEmptyValue, setShowEmptyValue] = useState(lookup.showEmptyValue || false)
    const [sectionTemplateShowModal, setSectionTemplateShowModal] = useState<boolean>(false)

    const [currentMacro, setCurrentMacro] = useState(lookup.macro)
    const [currentFixedValues, setCurrentFixedValues] = useState(lookup.fixedValues)

    function handleLookupStatus() {
        setlookupStatus(!lookupStatus)
        onChange(!lookupStatus, {
            ...lookup,
            showEmptyValue,
            fixedValues: currentFixedValues,
            macro: currentMacro
        })
    }

    const tooltip =
        'Once you have saved your changes, you can easily navigate to the "Preview and Test" tab to visualize how it will be displaced in your report.'

    const emptyValueToggleText = showEmptyValue
        ? 'When turned on, means that, regardless of whether a value in the selected lookup is empty, its content will be displayed as a blank space in the report.'
        : 'When turned off, means that, if a value in the selected lookup is empty, its content will not be displayed in the report.'

    const optionTemplateModalToolTip =
        'Select a Lookup option, to enable Reports Maker to utilize its values as filters to populate the template with the corresponding data'

    const emptyValueTemplateModalToolTip =
        'The empty value rule enables the Report Maker to disregard values in the lookup that do not have data to be displayed, resulting in empty placeholders in the report. You have the option to choose whether to allow the display of empty information or not'

    const exceptionRuleTemplateModalToolTip =
        'In certain cases, an exception can be made to the empty value rule based on the significance of a particular value to your business. If a value holds critical importance, you have the option to display its data even when it has no information'

    const resetManualReportModalData = (): void | null => {
        setShowEmptyValue(lookup.showEmptyValue || false)
        setCurrentMacro(lookup.macro)
        setCurrentFixedValues(lookup.fixedValues)
        setSectionTemplateShowModal(false)
    }

    return (
        <LookupButtonContainer color={lookupStatus ? 'blue' : 'gray'} lookupStatus={lookupStatus}>
            <div onClick={() => setSectionTemplateShowModal(true)}>
                <div className="section-template">
                    <i className="far fa-th-large addIconActive" />

                    <span>{i18n('Use section as template')}</span>
                </div>
                <div className="section-description">
                    <p>
                        {i18n(
                            'Discover a convenient and time-saving feature: select a lookup and seamlessly integrate its content into the section layout with automated precision.'
                        )}
                    </p>
                    <p>{i18n('Update or turn off whenever you want.')}</p>
                </div>
                <div className="toggleStatus" onClick={event => event.stopPropagation()}>
                    <Toggle
                        value={lookupStatus}
                        onChange={handleLookupStatus}
                        titleComponent={<span>{i18n(lookupStatus ? 'sectionTemplateActive' : 'Inactive')}</span>}
                    />
                    <div className="toolTipContainer">
                        {lookupStatus ? <CustomToolTip tooltip={i18n(tooltip)} /> : null}
                    </div>
                </div>
            </div>

            <TemplateModal
                show={sectionTemplateShowModal}
                onHide={() => resetManualReportModalData()}
                animation={false}
                className="modal-sm"
                width={435}
                height={450}
            >
                <TemplateModal.Header closeButton>
                    <TemplateModal.Title>{i18n('Use section as template function configuration')}</TemplateModal.Title>
                </TemplateModal.Header>

                <TemplateModal.Body>
                    <span className="innerTitleText">{i18n('Lookup Table (Static Data)')}</span>
                    <div>
                        <span className="inputHeaderText">
                            {i18n('Select an Option')}:
                            <CustomToolTip tooltip={i18n(optionTemplateModalToolTip)} widthInPX={231} />
                        </span>
                        <ListAsync
                            placeholder={i18n('Select a lookup…')}
                            getApi={getLookups}
                            value={currentMacro ? ({ value: currentMacro } as OptionType) : null}
                            onChange={(option, actionMeta) => {
                                const selectedOption = option as OptionType

                                if (
                                    actionMeta.action === 'remove-value' ||
                                    actionMeta.action === 'create-option' ||
                                    actionMeta.action === 'select-option'
                                ) {
                                    if (selectedOption.value !== currentMacro) {
                                        setlookupStatus(true)
                                        setCurrentMacro(selectedOption.value as string)
                                        setCurrentFixedValues({})
                                    }
                                }
                                if (actionMeta.action === 'clear') {
                                    setlookupStatus(false)
                                    setCurrentMacro('')
                                    setCurrentFixedValues({})
                                }
                            }}
                            callback={callbackLookup}
                            disabled={false}
                            isClearable
                            editMode={true}
                            useLabels={true}
                            selectValueById={true}
                            styles={CustomListAsyncLocal}
                        />
                    </div>

                    <div className="separator" />

                    <span className="innerTitleText">
                        {i18n('Empty value Rule')}
                        <CustomToolTip tooltip={i18n(emptyValueTemplateModalToolTip)} widthInPX={318} />
                    </span>
                    <span className="inputHeaderText">{i18n('Permission status')}:</span>

                    <Toggle
                        value={showEmptyValue}
                        onChange={() => setShowEmptyValue(!showEmptyValue)}
                        titleComponent={<span className="toggleText">{i18n('Showing empty value')}</span>}
                    />

                    <p className="emptyValueTooltip">{i18n(emptyValueToggleText)}</p>

                    {!showEmptyValue && (
                        <div>
                            <span className="inputHeaderText">
                                {i18n('Exception to the rule (optional)')}:
                                <CustomToolTip tooltip={i18n(exceptionRuleTemplateModalToolTip)} widthInPX={318} />
                            </span>
                            <ListAsync
                                placeholder={i18n('Select a value to be an exception to the rule...')}
                                value={
                                    currentFixedValues !== undefined && Object.entries(currentFixedValues).length > 0
                                        ? Object.entries(currentFixedValues).map(([label, value]) => {
                                              return { label, value } as OptionType
                                          })[0]
                                        : null
                                }
                                onChange={(option: OptionType, actionMeta) => {
                                    const newFixedValues = {}
                                    if (
                                        actionMeta.action === 'remove-value' ||
                                        actionMeta.action === 'create-option' ||
                                        actionMeta.action === 'select-option'
                                    ) {
                                        newFixedValues[option.label as string] = option.value
                                        setCurrentFixedValues(newFixedValues)
                                    }
                                    if (actionMeta.action === 'clear') {
                                        setCurrentFixedValues(newFixedValues)
                                    }
                                }}
                                disabled={false}
                                getApi={() => new Promise(resolve => resolve(getLookupValues(currentMacro)))}
                                isMulti={false}
                                callback={(entries: Entries[]): OptionType[] => {
                                    return entries
                                        ?.map(({ key, value }) => ({ value: value, label: key }))
                                        .reduce((result: OptionType[], curr) => {
                                            const existing = result.find(elem => elem.label === curr.label)
                                            if (existing) existing.value = [...(existing.value as string[]), curr.value]
                                            else result.push({ ...curr, value: [curr.value] })

                                            return result
                                        }, [])
                                }}
                                isClearable={true}
                                editMode={true}
                                styles={CustomListAsyncLocal}
                            />
                        </div>
                    )}
                </TemplateModal.Body>
                <TemplateModal.Footer>
                    <TemplateModal.ModalActions
                        onSubmit={() => {
                            onChange(lookupStatus, {
                                ...lookup,
                                showEmptyValue,
                                fixedValues: currentFixedValues,
                                macro: currentMacro
                            })
                            setSectionTemplateShowModal(false)
                            GlobalNotification.success(
                                <LookupNotification>
                                    <div className="lookupNotificationTitle">{i18n('Your settings were applied!')}</div>
                                    <div className="lookupNotificationDescription">
                                        {i18n('You can continue setting your content configuration.')}
                                    </div>
                                </LookupNotification>,
                                5
                            )
                        }}
                        submitText={i18n('Apply')}
                        submitBtnClassName="btn btn-primary saveButton"
                        cancelBtnClassName="discardButton"
                        onCancel={() => resetManualReportModalData()}
                        cancelText={i18n('Discard')}
                    />
                </TemplateModal.Footer>
            </TemplateModal>
        </LookupButtonContainer>
    )
}

const LookupNotification = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    /* padding: 30px; */

    .lookupNotificationTitle {
        color: ${({ theme }) => theme.white} !important;
        font-family: ${({ theme }) => theme['font-semibold']};
        /* font-feature-settings: 'clig' off, 'liga' off; */
        font-size: 16px;
        font-style: normal;
        font-weight: 600;
        line-height: 16px; /* 100% */
        i,
        .far {
            margin-right: 8px !important;
        }
    }

    .lookupNotificationDescription {
        color: ${({ theme }) => theme.white} !important;
        font-family: ${({ theme }) => theme['font-semibold']};
        /* font-feature-settings: 'clig' off, 'liga' off; */
        font-size: 14px;
        font-style: normal;
        font-weight: 400;
        line-height: 16px; /* 114.286% */
    }
`
